import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, Search, X } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { PostcodeSearch } from "@/components/silver/tool/PostcodeSearch";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";
import { openWorkspace } from "@/lib/urlState";
import { playPop, primeAudio } from "@/lib/popSound";

const AUTO_OPEN_MS = 5000;
const AUTO_OPEN_KEY = "ncp-search-auto-opened";

function SearchPanel({ mode, onClose, autoFocus = true }) {
  const { areas, audience, filters } = useSilver();
  const landing = mode === "landing";
  const begin = () => { openWorkspace(areas, filters); onClose(); };
  return (
    <motion.div role="dialog" aria-label="Search your area" className="fixed inset-x-4 bottom-[88px] z-40 sm:inset-x-auto sm:bottom-24 sm:right-7 sm:w-[440px]"
      initial={{ opacity: 0, y: 36, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97, transition: { duration: 0.22 } }}
      transition={{ type: "spring", stiffness: 300, damping: 28 }} data-testid="search-panel">
      <div className="glass-strong relative p-6 sm:p-7">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[radial-gradient(circle,rgba(150,193,31,0.22),transparent_65%)]" aria-hidden="true" />
        <button type="button" onClick={onClose} aria-label="Close search" className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-white/30 hover:text-white" data-testid="search-panel-close-btn">
          <X className="h-4 w-4" />
        </button>
        <p className="eyebrow">{landing ? "Start here" : "Your patch"}</p>
        <h3 className="mt-1.5 text-2xl font-extrabold tracking-[-0.03em] text-white">{landing ? "Search your area" : "Edit your postcodes"}</h3>
        <p className="mt-1 text-[13px] text-white/55">{landing ? "Add the postcode districts your branch covers — one, or fifty." : "Add or remove districts. Your audience updates instantly."}</p>
        <div className="mt-5"><PostcodeSearch variant="card" direction="up" autoFocus={autoFocus} /></div>
        <p className="min-h-[20px] text-[13px] text-white/60" data-testid="search-panel-summary">
          {areas.length
            ? <><span className="num font-semibold text-white">{fmtInt(audience.silverTotal)}</span> homeowner households across {areas.length} district{areas.length > 1 ? "s" : ""}</>
            : "Try RG40, RG41 or SW18"}
        </p>
        {landing ? (
          <>
            <button type="button" className="btn-primary mt-4 w-full !py-4 text-[15px]" disabled={!areas.length} onClick={begin} data-testid="search-panel-begin-btn">
              Let's start marketing <ArrowUpRight className="h-5 w-5" />
            </button>
            <p className="mt-2.5 text-center font-ui text-[11px] text-white/40">Opens your audience workspace in a new tab</p>
          </>
        ) : (
          <button type="button" className="btn-primary mt-4 w-full !py-4 text-[15px]" onClick={onClose} data-testid="search-panel-done-btn">
            Done <Check className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

// Floating magnifier. On the landing page it starts the journey; in the workspace it edits the patch.
export default function SearchFab({ mode = "landing" }) {
  const { phase, areas, searchOpen: open, setSearchOpen: setOpen } = useSilver();
  const [sectionInView, setSectionInView] = useState(false);
  const [ready, setReady] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const landing = mode === "landing";
  const touchedRef = useRef(false);
  const stateRef = useRef({ areas: 0, sectionInView: false });
  stateRef.current = { areas: areas.length, sectionInView };
  if (open) touchedRef.current = true;

  useEffect(() => {
    const t = setTimeout(() => setReady(true), landing ? 1600 : 600);
    const el = landing ? document.getElementById("build") : null;
    const io = el ? new IntersectionObserver(([e]) => setSectionInView(e.isIntersecting), { threshold: 0.06 }) : null;
    io?.observe(el);
    return () => { clearTimeout(t); io?.disconnect(); };
  }, [landing]);

  // Unlock audio on the first gesture, then pop the search open once, 5s after the homepage loads.
  useEffect(() => {
    if (!landing) return undefined;
    const prime = () => primeAudio();
    const events = ["pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, prime, { passive: true }));
    let timer = null;
    if (!sessionStorage.getItem(AUTO_OPEN_KEY)) {
      timer = setTimeout(() => {
        if (touchedRef.current || stateRef.current.areas || stateRef.current.sectionInView) return;
        sessionStorage.setItem(AUTO_OPEN_KEY, "1");
        setAutoOpened(true);
        setOpen(true);
        playPop();
      }, AUTO_OPEN_MS);
    }
    return () => {
      events.forEach((e) => window.removeEventListener(e, prime));
      if (timer) clearTimeout(timer);
    };
  }, [landing, setOpen]);

  useEffect(() => { if (!open) setAutoOpened(false); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const allowed = landing ? !sectionInView : phase === "define";
  const show = ready && (open || allowed);
  useEffect(() => { if (!allowed && open) setOpen(false); }, [allowed, open, setOpen]);

  return (
    <>
      <AnimatePresence>{open && <SearchPanel key="panel" mode={mode} onClose={() => setOpen(false)} autoFocus={!autoOpened} />}</AnimatePresence>
      <AnimatePresence>
        {landing && show && (
          <motion.button type="button" onClick={() => setOpen(!open)} aria-label={open ? "Close search" : landing ? "Search your area" : "Edit postcodes"} aria-expanded={open}
            className="group fixed bottom-5 right-5 z-40 flex items-center gap-3.5 rounded-full border border-white/10 bg-[rgba(12,30,16,0.74)] py-1.5 pl-5 pr-1.5 text-white shadow-[0_28px_60px_-20px_rgba(0,0,0,0.75)] backdrop-blur-2xl transition-colors hover:border-[#96C11F]/40 sm:bottom-7 sm:right-7"
            initial={{ opacity: 0, y: 28, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.92, transition: { duration: 0.25 } }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }} whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }} data-testid="search-area-fab">
            <span className="text-left leading-tight">
              <span className="block font-ui text-[10px] font-semibold uppercase tracking-[0.14em] text-[#C6EE5A]">{open ? "Ready when you are" : landing ? "Start here" : "Your patch"}</span>
              <span className="block text-[14px] font-semibold">{open ? "Close" : landing ? "Search your area" : "Edit postcodes"}</span>
            </span>
            <span className="relative grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-[#96C11F] to-[#7AA818] text-[#12290F] shadow-[0_10px_30px_-10px_rgba(150,193,31,0.85)] transition-transform duration-500 ease-out group-hover:-rotate-12 group-hover:scale-105">
              {!open && landing && <span className="absolute inset-0 rounded-full border border-[#C6EE5A]/70 animate-pulse-ring" aria-hidden="true" />}
              <span className="absolute inset-[3px] rounded-full bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.5),transparent_45%)]" aria-hidden="true" />
              {open ? <X className="relative h-5 w-5" strokeWidth={2.6} /> : <Search className="relative h-5 w-5" strokeWidth={2.6} />}
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
